'use client'

import { Media } from '@/components/Media'
import styles from '@/blocks/Team1Block/Component.module.scss'
import type { Media as MediaType } from '@/payload-types'
import { Github, Linkedin, Twitter, Mail, Globe } from 'lucide-react'

type SocialLink = {
  platform: 'linkedin' | 'twitter' | 'github' | 'email' | 'website'
  url: string
  id?: string
}

type TeamMember = {
  name: string
  role: string
  avatar: string | MediaType
  bio?: string
  socialLinks?: SocialLink[]
  id?: string
}

type Props = {
  heading?: string
  description?: string
  members: TeamMember[]
}

const getSocialIcon = (platform: string) => {
  switch (platform) {
    case 'linkedin':
      return <Linkedin className={styles.socialIcon} />
    case 'twitter':
      return <Twitter className={styles.socialIcon} />
    case 'github':
      return <Github className={styles.socialIcon} />
    case 'email':
      return <Mail className={styles.socialIcon} />
    case 'website':
      return <Globe className={styles.socialIcon} />
    default:
      return <Globe className={styles.socialIcon} />
  }
}

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const ClientTeam1Block = ({
  heading = 'Team',
  description = 'Our diverse team of experts brings together decades of experience in design, engineering, and product development.',
  members,
}: Props) => {
  return (
    <section className={styles.teamSection}>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <h2 className={styles.heading}>{heading}</h2>
          <p className={styles.description}>{description}</p>
        </div>

        <div className={styles.membersGrid}>
          {members.map((member, index) => (
            <div key={member.id || index} className={styles.memberCard}>
              <div className={styles.avatar}>
                {typeof member.avatar === 'string' ? (
                  <div
                    className={styles.avatarImage}
                    style={{
                      backgroundImage: `url(${member.avatar})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                ) : (
                  <Media
                    resource={member.avatar}
                    className={styles.avatarImage}
                    imgClassName={styles.avatarImage}
                    alt={member.name}
                  />
                )}
                {!member.avatar && (
                  <div className={styles.avatarFallback}>{getInitials(member.name)}</div>
                )}
              </div>{' '}
              <p className={styles.memberName}>{member.name}</p>
              <p className={styles.memberRole}>{member.role}</p>
              {member.bio && <p className={styles.memberBio}>{member.bio}</p>}
              {member.socialLinks && member.socialLinks.length > 0 && (
                <div className={styles.socialLinks}>
                  {member.socialLinks.map((link, linkIndex) => (
                    <a
                      key={link.id || linkIndex}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                      aria-label={`${member.name} on ${link.platform}`}
                    >
                      {getSocialIcon(link.platform)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ClientTeam1Block
